import axios, {AxiosHeaders, CanceledError, HttpStatusCode} from "axios";
import AttorneyHubAPIService from "common/services/api/attorney-hub-api-service";
import Guid from "common/values/guid/guid";
import UpdateEntityMemberAPIRequest
  from "legal-entities/entities/entity-member/api/request-contracts/update-entity-member-api-request";
import EntityMembershipsUserInfoAPIResponse
  from "legal-entities/entities/entity-member/api/response-contracts/entity-membership-user-info-api-response";
import EntityMember, {
  EntityMemberNotFoundException,
  UnauthorizedEntityMemberAccessError
} from "legal-entities/entities/entity-member/entity-member";
import LegalEntity, {LegalEntityNotFoundError} from "legal-entities/entities/legal-entity/legal-entity";
import CreateUserEntityMemberInvitationAPIRequest
  from "legal-entities/entities/user-entity-member-invitation/api/request-contracts/create-user-entity-member-invitation-api-request";
import UserEntityMemberInvitation
  from "legal-entities/entities/user-entity-member-invitation/user-entity-member-invitation";
import Session from "users/session/session";
import MemberUserInfoAPIResponse from "./response-contracts/member-user-info-api-response";

export default class EntityMemberAPIService {
  private readonly headers: AxiosHeaders = new AxiosHeaders();

  constructor(session: Readonly<Session>) {
    if (!session.authToken) {
      throw new Error("Cannot create EntityMemberAPIService without session.");
    }
    this.headers.set("Authorization", `Bearer ${session.authToken.value}`);
  }

  async createUserEntityMemberInvitation(
    invitation: UserEntityMemberInvitation
  ): Promise<UserEntityMemberInvitation> {
    const url = new URL(
      "/legal-entities/user-entity-member-invitations", 
      AttorneyHubAPIService.apiBaseUrl
    );
    try {
      const response = await axios.post(
        url.toString(),
        new CreateUserEntityMemberInvitationAPIRequest(invitation),
        {
          headers: this.headers.concat({ "Content-Type": "application/json" })
        }
      );
      invitation.id = new Guid(response.data);
      return invitation;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.BadRequest)
        throw new InvalidInvitationError(error.response.data);
      throw new EntityMemberAPIServiceError("createUserEntityMemberInvitation", error);
    }
  }

  async getLegalEntityMembersUserInfo(
    legalEntity: LegalEntity, 
    abortController?: AbortController
  ): Promise<EntityMember[]> {
    const url = new URL(
      `/legal-entities/${legalEntity.id.value}/members/`, 
      AttorneyHubAPIService.apiBaseUrl
    );
    try {
      const response = await axios.get(
        url.toString(),
        {
          headers: this.headers,
          signal: abortController?.signal
        }
      );
      const entityMembers: EntityMember[] = [];
      const responseData = Object.assign(new EntityMembershipsUserInfoAPIResponse(), response.data);
      for (const userData of responseData.membershipUsers as unknown[]) {
        const entityMember = Object.assign(new MemberUserInfoAPIResponse(), userData).deserialize();
        entityMembers.push(entityMember);
      }
      return entityMembers;
    } catch (error) {
      if (error instanceof CanceledError)
        throw error;
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.Forbidden)
        throw new UnauthorizedEntityMemberAccessError(legalEntity.id);
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound)
        throw new LegalEntityNotFoundError(legalEntity.id);
      throw new EntityMemberAPIServiceError("getLegalEntityMembersUserInfo", error);
    }
  }

  async updateEntityMember(
    originalMember: EntityMember, 
    updatedMember: EntityMember
  ):  Promise<void> {
    const url = new URL(
      `/legal-entities/${originalMember.entityId.value}/members/${originalMember.memberId.value}`,
      AttorneyHubAPIService.apiBaseUrl
    );
    try {
      await axios.patch(
        url.toString(),
        new UpdateEntityMemberAPIRequest(originalMember, updatedMember).payload,
        {
          headers: this.headers
        }
      );
    } catch (error) {
      if (!axios.isAxiosError(error) || !error.response) return;
      switch (error.response.status) {
        case HttpStatusCode.BadRequest:
        case HttpStatusCode.Forbidden:
          throw new InvalidMemberUpdateException(error.response.data);
        case HttpStatusCode.NotFound:
          throw new EntityMemberNotFoundException(originalMember.entityId);
        default:
          throw new EntityMemberAPIServiceError("updateEntityMember", error);
      }
    }
  }
}

export class InvalidInvitationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InvalidInvitationError";
  }
}

export class InvalidMemberUpdateException extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InvalidMemberUpdateException";
  }
}

export class EntityMemberAPIServiceError extends Error {
  methodName: string;
  error: unknown;
  constructor(methodName: string, error: unknown) {
    super(`Error in EntityMemberAPIService.${methodName}: ${error instanceof Error ? error.message : String(error)}`);
    this.methodName = methodName;
    this.error = error;
  }
}
